import { Link } from "react-router-dom";
import { SectionHeading } from "@/components/SectionHeading";
import { STORE } from "@/constants/testIds";
import { CONTACT_EMAIL } from "@/data/content";

const Container = ({ children, className = "" }) => (
  <div className={`mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

const Block = ({ title, children }) => (
  <section className="mt-7 border-t border-[color:rgba(10,27,51,0.10)] pt-7 first:mt-0 first:border-t-0 first:pt-0">
    <h2 className="font-[var(--font-heading)] text-lg font-semibold text-[var(--ibp-ink)]">
      {title}
    </h2>
    <div className="mt-3 space-y-3 text-sm leading-6 text-[color:rgba(11,18,32,0.72)]">
      {children}
    </div>
  </section>
);

export default function Privacy() {
  return (
    <div data-testid={STORE.privacyPage} className="bg-[var(--ibp-offwhite)]">
      <section className="bg-[var(--ibp-navy-950)] py-12 text-white sm:py-16">
        <Container>
          <SectionHeading
            tone="navy"
            kicker="Policy"
            title="Privacy Policy"
            subtitle="How Illinois Broker Prep collects, uses, and protects your information."
          />
        </Container>
      </section>

      <section className="py-14 sm:py-20">
        <Container>
          <div className="mx-auto max-w-3xl rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-7 shadow-[0_10px_30px_rgba(7,20,39,0.06)] sm:p-9">
            <p className="text-xs leading-5 text-[color:rgba(11,18,32,0.55)]">
              Last updated: June 2026.
            </p>
            <p className="mt-4 text-sm leading-6 text-[color:rgba(11,18,32,0.72)]">
              This policy applies to Illinois Broker Prep, a study tool
              operated by Coursewright ("Coursewright," "we," "us," or "our").
            </p>

            <div className="mt-7">
              <Block title="Who we are">
                <p>
                  Illinois Broker Prep provides an independent study aid for
                  people preparing for the Illinois real estate broker exam. We
                  are not affiliated with PSI, IDFPR, the State of Illinois, or
                  any real estate commission.
                </p>
              </Block>

              <Block title="Information we collect">
                <p>We collect only what we need to provide the service:</p>
                <ul className="list-disc space-y-1.5 pl-5">
                  <li>
                    <strong>Account and contact details</strong> — your name and
                    email when you create an account, use the contact form, buy
                    access, or request diagnostic results.
                  </li>
                  <li>
                    <strong>Practice activity</strong> — questions attempted,
                    answers selected, scores, bookmarks, and missed questions,
                    so we can show your progress.
                  </li>
                  <li>
                    <strong>Account access records</strong> — your free or full
                    access status, purchase reference, sign-in sessions, and
                    short-lived verification-code records used to secure your account.
                  </li>
                  <li>
                    <strong>Basic usage analytics</strong> — anonymous or
                    pseudonymous events that help us improve the product.
                  </li>
                  <li>
                    <strong>Quality feedback</strong> — question reports and
                    comments you choose to submit about the study experience.
                  </li>
                  <li>
                    <strong>Live-review preferences</strong> — your expected exam
                    date, preferred timing, difficult topics, and support format
                    when you voluntarily join a priority list.
                  </li>
                </ul>
              </Block>

              <Block title="Payments">
                <p>
                  Purchases are processed by a secure third-party checkout
                  provider. We do not collect, see, or store your payment card
                  details. The checkout provider handles checkout and billing
                  in accordance with its own privacy practices.
                </p>
              </Block>

              <Block title="How we use your information">
                <ul className="list-disc space-y-1.5 pl-5">
                  <li>To send your diagnostic results and a study plan.</li>
                  <li>To provide and personalize the practice experience.</li>
                  <li>To respond to your support messages.</li>
                  <li>To manage priority requests and announce relevant live-review options.</li>
                  <li>To review reported questions and improve study content.</li>
                  <li>
                    To publish customer feedback only when you give permission
                    and an administrator approves it.
                  </li>
                  <li>To maintain, secure, and improve the product.</li>
                </ul>
              </Block>

              <Block title="Service providers">
                <p>
                  We share limited information with providers that help us run
                  the service, including a checkout provider and an email
                  provider used to send results and notifications. These
                  providers process data on our behalf.
                </p>
              </Block>

              <Block title="Data retention &amp; your choices">
                <p>
                  We keep information for as long as needed to provide the
                  service. You may request access to, correction of, or
                  deletion of your information by contacting us. You can opt out
                  of marketing emails at any time.
                </p>
              </Block>

              <Block title="Contact">
                <p>
                  Questions about this policy? Email{" "}
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="font-semibold text-[#0F3D8A] hover:underline"
                  >
                    {CONTACT_EMAIL}
                  </a>{" "}
                  or use our{" "}
                  <Link to="/contact" className="font-semibold text-[#0F3D8A] hover:underline">
                    contact form
                  </Link>
                  .
                </p>
              </Block>
            </div>

            <p className="mt-8 text-xs leading-5 text-[color:rgba(11,18,32,0.55)]">
              Illinois Broker Prep is an independent study aid. Not affiliated
              with PSI, IDFPR, the State of Illinois, or any real estate
              commission. No actual exam questions. No pass guarantee.
            </p>
          </div>
        </Container>
      </section>
    </div>
  );
}
